type Primitive = string | number | boolean | null

type AnalyticsProperties = Record<string, Primitive>

type PostHogClient = {
  capture: (event: string, properties?: Record<string, unknown>) => void
  identify: (userId: string, properties?: Record<string, unknown>) => void
  reset: () => void
}

declare global {
  interface Window {
    posthog?: PostHogClient
  }
}

const ANALYTICS_ENABLED = (import.meta.env.VITE_ANALYTICS_ENABLED || 'false') === 'true'
const ANALYTICS_DEBUG = (import.meta.env.VITE_ANALYTICS_DEBUG || 'false') === 'true'
const IDENTIFIED_TRACKING_ENABLED = (import.meta.env.VITE_IDENTIFIED_TRACKING_ENABLED || 'false') === 'true'
const POSTHOG_PROJECT_ID = import.meta.env.VITE_POSTHOG_PROJECT_ID

const FORBIDDEN_PROPERTY_KEYS = new Set([
  'auth_id',
  'patient_id',
  'provider_id',
  'appointment_id',
  'email',
  'phone',
  'message',
  'content',
  'medical_info',
  'medical_history',
  'allergies',
  'current_symptoms',
  'current_medications',
  'latitude',
  'longitude',
  'location',
  'address',
])

const ALLOWED_IDENTIFY_PROPERTY_KEYS = new Set([
  'user_role',
  'platform',
  'app_version',
])

export function getAmountBucket(amount: number): string {
  if (amount < 1000) return 'lt_1000'
  if (amount < 5000) return '1000_4999'
  if (amount < 10000) return '5000_9999'
  if (amount < 25000) return '10000_24999'
  if (amount < 50000) return '25000_49999'
  return 'ge_50000'
}

function getPostHogClient(): PostHogClient | null {
  if (typeof window === 'undefined') return null
  return window.posthog ?? null
}

class PostHogAnalyticsService {
  private initialized = false
  private enabled = ANALYTICS_ENABLED

  init() {
    if (this.initialized) return
    this.initialized = true

    if (!getPostHogClient() && ANALYTICS_DEBUG) {
      console.warn('[Analytics] PostHog snippet not found on window.posthog')
    }
  }

  setEnabled(value: boolean) {
    this.enabled = value
  }

  getProjectId(): string | null {
    if (!POSTHOG_PROJECT_ID) return null
    return POSTHOG_PROJECT_ID
  }

  identifyAuthenticatedUser(userId: string, properties?: AnalyticsProperties) {
    try {
      const client = getPostHogClient()
      if (!client || !this.enabled || !IDENTIFIED_TRACKING_ENABLED || !userId) return

      const sanitized = this.sanitizeIdentifyProperties(properties)
      client.identify(userId, sanitized)

      if (ANALYTICS_DEBUG) {
        console.log('[Analytics] identify', userId, sanitized)
      }
    } catch (error) {
      console.warn('Failed to identify analytics user:', error)
    }
  }

  resetUserSession() {
    try {
      const client = getPostHogClient()
      if (!client || !this.enabled) return
      client.reset()

      if (ANALYTICS_DEBUG) {
        console.log('[Analytics] User session reset')
      }
    } catch (error) {
      console.warn('Failed to reset analytics user session:', error)
    }
  }

  resetAnonymousSession() {
    this.resetUserSession()
  }

  capture(event: string, properties?: AnalyticsProperties) {
    try {
      const client = getPostHogClient()
      if (!client || !this.enabled) return
      const sanitized = this.sanitizeProperties(properties)
      client.capture(event, sanitized)

      if (ANALYTICS_DEBUG) {
        console.log('[Analytics] capture', event, sanitized)
      }
    } catch (error) {
      console.warn('Failed to capture analytics event:', error)
    }
  }

  screen(screenName: string, properties?: AnalyticsProperties) {
    try {
      const client = getPostHogClient()
      if (!client || !this.enabled) return
      const sanitized = this.sanitizeProperties(properties)
      client.capture('$screen', {
        $screen_name: screenName,
        ...sanitized,
      })

      if (ANALYTICS_DEBUG) {
        console.log('[Analytics] screen', screenName, sanitized)
      }
    } catch (error) {
      console.warn('Failed to capture analytics screen:', error)
    }
  }

  private sanitizeProperties(properties?: AnalyticsProperties): AnalyticsProperties {
    if (!properties) return {}

    const sanitized: AnalyticsProperties = {}

    Object.entries(properties).forEach(([key, value]) => {
      if (!key || FORBIDDEN_PROPERTY_KEYS.has(key)) {
        return
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
        sanitized[key] = value
      }
    })

    return sanitized
  }

  private sanitizeIdentifyProperties(properties?: AnalyticsProperties): AnalyticsProperties {
    if (!properties) return {}

    const sanitized: AnalyticsProperties = {}

    Object.entries(properties).forEach(([key, value]) => {
      if (!key || !ALLOWED_IDENTIFY_PROPERTY_KEYS.has(key)) {
        return
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
        sanitized[key] = value
      }
    })

    return sanitized
  }
}

export const analyticsService = new PostHogAnalyticsService()
